/**
 * Dutch Translations — Impact Core i18n
 *
 * Flat key-value structure. Keys use dot notation for namespacing.
 * Coverage: navigation, actions, statuses, dashboard labels, form labels.
 */

export const nl: Record<string, string> = {
  // Navigation
  'nav.dashboard': 'Dashboard',
  'nav.funds': 'Fondsen',
  'nav.investments': 'Investeringen',
  'nav.investees': 'Deelnemingen',
  'nav.esg': 'ESG Beoordeling',
  'nav.compliance': 'Naleving',
  'nav.reports': 'Rapporten',
  'nav.settings': 'Instellingen',
  'nav.users': 'Gebruikers',
  'nav.sfdr': 'SFDR',
  'nav.pai': 'PAI Indicatoren',
  'nav.taxonomy': 'EU-taxonomie',
  'nav.filings': 'Rapportages',
  'nav.calendar': 'Kalender',
  'nav.submit': 'Gegevens indienen',
  'nav.submissions': 'Mijn inzendingen',
  'nav.benchmarks': 'Benchmarks',
  'nav.messages': 'Berichten',
  'nav.portfolio': 'Portefeuille',
  'nav.performance': 'Fondsprestaties',
  'nav.esgSummary': 'ESG Samenvatting',
  'nav.pipeline': 'Pijplijn',
  'nav.dueDiligence': 'Due Diligence',
  'nav.impact': 'Impactmetingen',
  'nav.sdg': 'SDG-mapping',
  'nav.audit': 'Auditspoor',
  'nav.snapshots': 'Datamomentopnamen',
  'nav.knowledgeGraph': 'Kennisgraaf',
  'nav.geographic': 'Geografisch',
  'nav.bi': 'BI Studio',
  'nav.ai': 'AI Assistent',
  'nav.tasks': 'Taken',
  'nav.csrd': 'CSRD',
  'nav.regulatory': 'Regelgevingsmonitor',

  // Common Actions
  'action.create': 'Aanmaken',
  'action.edit': 'Bewerken',
  'action.delete': 'Verwijderen',
  'action.save': 'Opslaan',
  'action.cancel': 'Annuleren',
  'action.submit': 'Indienen',
  'action.approve': 'Goedkeuren',
  'action.reject': 'Afwijzen',
  'action.download': 'Downloaden',
  'action.export': 'Exporteren',
  'action.import': 'Importeren',
  'action.filter': 'Filteren',
  'action.search': 'Zoeken',
  'action.refresh': 'Vernieuwen',
  'action.close': 'Sluiten',
  'action.back': 'Terug',
  'action.next': 'Volgende',
  'action.previous': 'Vorige',
  'action.viewAll': 'Alles bekijken',
  'action.viewDetails': 'Details bekijken',
  'action.generate': 'Genereren',
  'action.signOut': 'Uitloggen',
  'action.addNew': 'Nieuwe toevoegen',
  'action.invite': 'Uitnodigen',

  // Statuses
  'status.draft': 'Concept',
  'status.submitted': 'Ingediend',
  'status.validated': 'Gevalideerd',
  'status.approved': 'Goedgekeurd',
  'status.rejected': 'Afgewezen',
  'status.pending': 'In afwachting',
  'status.inProgress': 'In uitvoering',
  'status.overdue': 'Achterstallig',
  'status.compliant': 'Conform',
  'status.filed': 'Ingediend',
  'status.active': 'Actief',
  'status.inactive': 'Inactief',
  'status.closed': 'Gesloten',
  'status.fundraising': 'Fondsenwerving',
  'status.investing': 'Investeren',
  'status.harvesting': 'Oogst',
  'status.validating': 'Valideren',
  'status.review': 'In beoordeling',
  'status.monitoring': 'Monitoring',
  'status.exiting': 'Uitstap',
  'status.pipeline': 'Pipeline',
  'status.due_diligence': 'Due Diligence',
  'status.disbursed': 'Uitgekeerd',
  'status.restructured': 'Geherstructureerd',
  'status.matured': 'Vervallen',
  'status.written_off': 'Afgeschreven',
  'status.exited': 'Uitgestapt',
  'status.incomplete': 'Onvolledig',
  'status.open': 'Open',
  'status.in_progress': 'In uitvoering',
  'status.completed': 'Voltooid',
  'status.cancelled': 'Geannuleerd',
  'status.accepted': 'Geaccepteerd',
  'status.failed': 'Mislukt',

  // Dashboard Labels
  'dashboard.portfolioOverview': 'Portefeuilleoverzicht',
  'dashboard.complianceStatus': 'Nalevingsstatus',
  'dashboard.collectionProgress': 'Verzamelingsvoortgang',
  'dashboard.activityFeed': 'Activiteit',
  'dashboard.quickActions': 'Snelle acties',
  'dashboard.investmentPipeline': 'Investeringspijplijn',
  'dashboard.totalFunds': 'Totaal fondsen',
  'dashboard.aum': 'AUM',
  'dashboard.activeInvestments': 'Actieve investeringen',
  'dashboard.totalInvestees': 'Deelnemingen',
  'dashboard.recentActivity': 'Recente activiteit',

  // LP Dashboard Labels
  'lp.totalCommitted': 'Totaal gecommitteerd',
  'lp.capitalCalled': 'Opgevraagd kapitaal',
  'lp.distributed': 'Gedistribueerd',
  'lp.netPosition': 'Nettopositie',
  'lp.tvpi': 'TVPI',
  'lp.dpi': 'DPI',
  'lp.fundPerformance': 'Fondsprestaties',
  'lp.esgSummary': 'ESG Samenvatting',
  'lp.reportLibrary': 'Rapportbibliotheek',
  'lp.taxonomyAligned': 'Taxonomie-afgestemd',
  'lp.paiCoverage': 'PAI Dekking',
  'lp.portfolioPerformance': 'LP Portefeuilleprestaties',

  // Fund Labels
  'fund.name': 'Fondsnaam',
  'fund.code': 'Fondscode',
  'fund.vintage': 'Oprichtingsjaar',
  'fund.size': 'Fondsomvang',
  'fund.targetSize': 'Doelomvang',
  'fund.currency': 'Valuta',
  'fund.strategy': 'Strategie',
  'fund.status': 'Status',
  'fund.sfdrCategory': 'SFDR Categorie',
  'fund.geographyFocus': 'Geografische focus',
  'fund.sectorFocus': 'Sectorfocus',

  // Form Labels
  'form.name': 'Naam',
  'form.email': 'E-mail',
  'form.country': 'Land',
  'form.amount': 'Bedrag',
  'form.currency': 'Valuta',
  'form.date': 'Datum',
  'form.description': 'Beschrijving',
  'form.notes': 'Opmerkingen',
  'form.type': 'Type',
  'form.period': 'Periode',
  'form.year': 'Jaar',
  'form.required': 'Verplicht',
  'form.optional': 'Optioneel',

  // Table Headers
  'table.fund': 'Fonds',
  'table.commitment': 'Commitment',
  'table.called': 'Opgevraagd',
  'table.distributed': 'Gedistribueerd',
  'table.tvpi': 'TVPI',
  'table.status': 'Status',
  'table.regulation': 'Regelgeving',
  'table.filingType': 'Rapportagetype',
  'table.reportingPeriod': 'Periode',
  'table.createdAt': 'Aangemaakt',
  'table.actions': 'Acties',

  // Compliance
  'compliance.sfdr': 'SFDR',
  'compliance.taxonomy': 'EU-taxonomie',
  'compliance.pai': 'PAI',
  'compliance.dataCompleteness': 'Datavolledigheid',
  'compliance.nextDeadline': 'Volgende deadline',

  // Reports
  'report.quarterly': 'Kwartaalrapport',
  'report.annual': 'Jaarverslag',
  'report.paiStatement': 'PAI Verklaring',
  'report.taxonomyReport': 'Taxonomierapport',
  'report.generatedAt': 'Gegenereerd',
  'report.version': 'Versie',

  // General
  'general.loading': 'Laden...',
  'general.noData': 'Geen gegevens beschikbaar',
  'general.error': 'Er is een fout opgetreden',
  'general.retry': 'Opnieuw proberen',
  'general.total': 'Totaal',
  'general.page': 'Pagina',
  'general.of': 'van',
  'general.showing': 'Weergave',
  'general.results': 'resultaten',
  'general.language': 'Taal',
  'general.english': 'Engels',
  'general.french': 'Frans',

  // Tour
  'tour.takeTour': 'Rondleiding starten',
  'tour.skip': 'Overslaan',
  'tour.next': 'Volgende',
  'tour.prev': 'Vorige',
  'tour.finish': 'Voltooien',


  // Tour Content, Form Labels, Table Headers, Actions, Errors (i18n wiring)
  'tour.sidebarTitle': 'Navigatie',
  'tour.sidebarContent': 'Gebruik de zijbalk om te navigeren tussen portefeuillebeheer, ESG-gegevensverzameling, naleving, rapporten en analysetools.',
  'tour.kpisTitle': 'Portefeuilleoverzicht',
  'tour.kpisContent': 'Deze kaarten tonen uw portefeuille in één oogopslag — totaal fondsen, AUM, actieve investeringen en aantal deelnemingen.',
  'tour.actionsTitle': 'Snelle acties',
  'tour.actionsContent': 'Maak fondsen aan, voeg investeringen toe, nodig deelnemingen uit en genereer rapporten met één klik.',
  'tour.languageTitle': 'Taal',
  'tour.languageContent': 'Schakel tussen 24 officiële EU-talen. De gehele interface vertaalt direct.',
  'tour.aiTitle': 'AI Assistent',
  'tour.aiContent': 'Stel op elk moment vragen over uw portefeuille. De AI-assistent heeft volledige context over uw fondsen en investeringen.',
  'action.createFund': 'Fonds aanmaken',
  'action.editFund': 'Fonds bewerken',
  'action.addInvestment': 'Investering toevoegen',
  'action.addInvestee': 'Deelneming toevoegen',
  'action.editInvestee': 'Deelneming bewerken',
  'action.createTemplate': 'Sjabloon aanmaken',
  'action.createSubmission': 'Inzending aanmaken',
  'action.createFiling': 'Rapportage aanmaken',
  'action.generateReport': 'Rapport genereren',
  'action.inviteUser': 'Gebruiker uitnodigen',
  'action.sendInvitation': 'Uitnodiging verzenden',
  'action.saveChanges': 'Wijzigingen opslaan',
  'form.fundName': 'Fondsnaam',
  'form.fundCode': 'Fondscode',
  'form.sfdrCategory': 'SFDR Categorie',
  'form.baseCurrency': 'Basisvaluta',
  'form.targetSize': 'Doelomvang',
  'form.vintageYear': 'Oprichtingsjaar',
  'form.geographyFocus': 'Geografische focus',
  'form.sectorFocus': 'Sectorfocus',
  'form.investmentCode': 'Investeringscode',
  'form.instrumentType': 'Instrumenttype',
  'form.maturityDate': 'Vervaldatum',
  'form.sector': 'Sector',
  'form.countryCode': 'Landcode',
  'form.entityType': 'Entiteitstype',
  'form.region': 'Regio',
  'form.staffCount': 'Personeelsaantal',
  'form.borrowers': 'Leners',
  'form.contactName': 'Contactnaam',
  'form.contactEmail': 'Contact e-mail',
  'form.templateName': 'Sjabloonnaam',
  'form.templateType': 'Sjabloontype',
  'form.indicators': 'Indicatoren',
  'form.reportingPeriod': 'Rapportageperiode',
  'form.reportingYear': 'Rapportagejaar',
  'form.regulation': 'Regelgeving',
  'form.filingType': 'Rapportagetype',
  'form.reportType': 'Rapporttype',
  'form.emailAddress': 'E-mailadres',
  'form.role': 'Rol',
  'form.selectFund': 'Selecteer fonds...',
  'form.selectInvestee': 'Selecteer deelneming...',
  'table.code': 'Code',
  'table.investee': 'Deelneming',
  'table.instrument': 'Instrument',
  'table.amount': 'Bedrag',
  'table.currency': 'Valuta',
  'table.maturity': 'Vervaldatum',
  'table.name': 'Naam',
  'table.country': 'Land',
  'table.type': 'Type',
  'table.period': 'Periode',
  'table.completion': 'Voltooiing',
  'table.submitted': 'Ingediend',
  'table.version': 'Versie',
  'table.indicators': 'Indicatoren',
  'table.dueDate': 'Vervaldatum',
  'table.generated': 'Gegenereerd',
  'table.email': 'E-mail',
  'table.role': 'Rol',
  'error.createFailed': 'Aanmaken mislukt. Probeer opnieuw.',
  'error.updateFailed': 'Bijwerken mislukt. Probeer opnieuw.',
  'error.loadFailed': 'Gegevens laden mislukt.',

  // Sidebar Sections
  'sections.portfolio': 'PORTFOLIO',
  'sections.dataCollection': 'GEGEVENSVERZAMELING',
  'sections.intelligence': 'INTELLIGENCE',
  'sections.ecosystem': 'ECOSYSTEEM',
  'sections.admin': 'BEHEER',
  'sections.other': 'OVERIG',

  // Page Titles
  'pages.messagesTitle': 'Berichten',
  'pages.taxonomyTitle': 'EU-Taxonomie',
  'pages.biStudioTitle': 'BI Studio',
  'pages.knowledgeGraphTitle': 'Kennisgraaf',
  'pages.benchmarksTitle': 'Benchmarks',
  'pages.mcpTitle': 'MCP-serverintegratie',
  'pages.tasksTitle': 'Taken',
  'pages.submitDataTitle': 'ESG-gegevens indienen',

  // Empty States
  'empty.benchmarksComingSoon': 'Benchmarks binnenkort beschikbaar',

  // Labels
  'labels.sfdr': 'SFDR',
  'labels.euTaxonomy': 'EU-Taxonomie',
  'labels.csrd': 'CSRD',
  'labels.mifid': 'MiFID',
  'labels.other': 'Overig',
  'labels.critical': 'Kritiek',
  'labels.high': 'Hoog',
  'labels.medium': 'Gemiddeld',
  'labels.low': 'Laag',

  // Count / summary strings
  'count.fundsTotal': '{count} fund(s) total',
  'count.investmentsTotal': '{count} investment(s) total',
  'count.investeesTotal': '{count} investee(s) total',

  // Filter options
  'filter.allStatus': 'All Status',
  'filter.allSfdr': 'All SFDR',

  // Empty states
  'empty.noFundsYet': 'No funds yet. Create your first fund.',
  'empty.noFundsMatch': 'No funds match your filters.',
  'empty.noInvestmentsYet': 'No investments yet.',
  'empty.noInvestmentsMatch': 'No investments match your search.',
  'empty.noInvesteesYet': 'No investees yet.',
  'empty.noInvesteesMatch': 'No investees match your search.',
  'empty.noTemplatesYet': 'No ESG templates configured yet.',
  'empty.noSubmissionsYet': 'No ESG submissions yet.',
  'empty.noComplianceData': 'No SFDR compliance data available.',
  'empty.noFilingsYet': 'No compliance filings yet.',
  'empty.noReportsYet': 'No reports generated yet.',
  'empty.selectFundPai': 'Select a fund to view PAI indicators.',
  'empty.noPaiIndicators': 'No PAI indicators found for this fund.',
  'empty.selectFundCalendar': 'Select a fund to view compliance calendar.',
  'empty.noCalendarEvents': 'No upcoming events for this fund.',

  // Page subtitles
  'subtitle.esg': 'Templates and data submissions',
  'subtitle.compliance': 'SFDR, PAI indicators, and regulatory filings',
  'subtitle.reports': 'Generate and manage compliance reports',
  'subtitle.settings': 'Manage your account and preferences',

  // Tab labels
  'tab.templates': 'Templates',
  'tab.submissions': 'Submissions',
  'tab.sfdr': 'SFDR',
  'tab.paiIndicators': 'PAI Indicators',
  'tab.filings': 'Filings',
  'tab.reports': 'Reports',
  'tab.calendar': 'Calendar',

  // Compliance table headers
  'table.indicator': 'Indicator',
  'table.category': 'Category',
  'table.metric': 'Metric',
  'table.value': 'Value',
  'table.mandatory': 'Mandatory',
  'table.data': 'Data',
  'table.deadline': 'Deadline',
  'table.taxonomy': 'Taxonomy',

  // Yes/No
  'general.yes': 'Yes',
  'general.no': 'No',

  // Error messages
  'error.loadTemplates': 'Failed to load templates.',
  'error.loadSubmissions': 'Failed to load submissions.',
  'error.loadCompliance': 'Failed to load compliance data.',
  'error.loadPai': 'Failed to load PAI indicators.',
  'error.loadFilings': 'Failed to load filings.',
  'error.loadCalendar': 'Failed to load calendar events.',
  'error.createTemplate': 'Failed to create template. Please try again.',
  'error.createSubmission': 'Failed to create submission. Please try again.',
  'error.createFiling': 'Failed to create filing. Please try again.',
  'error.generateReport': 'Failed to generate report.',
  'error.templateNameRequired': 'Template name is required',
  'error.selectFund': 'Select a fund',
  'error.selectInvestee': 'Select an investee',
  'error.validAmount': 'Enter a valid amount',
  'error.reportingPeriodRequired': 'Reporting period is required',
  'error.validYearRequired': 'Valid year is required',
  'error.enterPeriod': 'Enter a period',
  'error.nameRequired': 'Name is required',
  'error.countryCodeRequired': 'Country code is required',
  'error.operationFailed': 'Operation failed.',
  'error.pleaseselectFund': 'Please select a fund',

  // Success messages
  'success.submissionCreated': 'Submission created successfully.',
  'success.profileSaved': 'Profile saved successfully.',

  // Relative time
  'time.justNow': 'Just now',
  'time.minutesAgo': '{count}m ago',
  'time.hoursAgo': '{count}h ago',
  'time.daysAgo': '{count}d ago',

  // Settings
  'settings.profile': 'Profile',
  'settings.organization': 'Organization',
  'settings.apiKeys': 'API Keys',
  'settings.notifications': 'Notifications',
  'settings.orgId': 'Org ID',
  'settings.tier': 'Tier',
  'settings.apiKeysDescription': 'Contact your organization administrator to manage API keys and integration credentials.',
  'settings.apiKeysNote': 'API keys provide programmatic access to Impact Core data. For security, key creation and revocation is restricted to organization admins.',
  'settings.notifReportAlerts': 'Report generation alerts',
  'settings.notifReportAlertsDesc': 'Email when a report is generated or fails',
  'settings.notifComplianceReminders': 'Compliance deadline reminders',
  'settings.notifComplianceRemindersDesc': 'Email reminders for upcoming filing deadlines',
  'settings.notifEsgUpdates': 'ESG submission updates',
  'settings.notifEsgUpdatesDesc': 'Email when investee submissions change status',
  'settings.notifInAppActivity': 'In-app activity feed',
  'settings.notifInAppActivityDesc': 'Show real-time activity notifications in header',

  // Data Completeness label
  'compliance.dataCompletenessLabel': 'Data Completeness',
};
